import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";

import style from "./bms-config.scss";
import { DownloadServerConfiguration } from "./BmsCommonInterfaces";
import { getComplementaryColor } from "./bmsUtils";
import Checkbox from "./form-components/BmsCheckboxComponent";
import InputComponent from "components/licenses/license-configuration/bms/form-components/BmsTextInputComponent";

interface FormValues {
    downloadAppPageConfiguration: DownloadServerConfiguration;
    selectedApp: string;
}

interface Props {
    formValues: FormValues;
    setFormValues: React.Dispatch<React.SetStateAction<FormValues>>;
    showAdvanced: boolean;
}

const DownloadServerConfigurationForm: React.FC<Props> = ({ formValues, setFormValues }) => {
    const { t } = useTranslation();
    const disableLinkEditing = formValues.selectedApp === "bmsApp" || formValues.selectedApp === "deviceInspectApp";
    const handleInputChange = (fieldName: string, value: string | boolean) => {
        setFormValues((prevFormValues) => ({
            ...prevFormValues,
            downloadAppPageConfiguration: {
                ...prevFormValues.downloadAppPageConfiguration,
                [fieldName]: value,
            },
        }));
    };

    return (
        <div>
            <span>
                <div className={classNames(style.header)}>
                    {" "}
                    {t("Configuration.downloadServerConfiguration.headers")}
                </div>
            </span>
            <div className={classNames(style.formSection)}>
                <InputComponent
                    labelKey={t("Configuration.downloadServerConfiguration.appIcon")}
                    inputId={"appIcon"}
                    value={formValues.downloadAppPageConfiguration.appIcon}
                    onChange={(newValue) => handleInputChange("appIcon", newValue)}
                ></InputComponent>

                <InputComponent
                    labelKey={t("Configuration.downloadServerConfiguration.headerLogo")}
                    inputId={"headerLogo"}
                    value={formValues.downloadAppPageConfiguration.headerLogo}
                    onChange={(newValue) => handleInputChange("headerLogo", newValue)}
                ></InputComponent>

                <InputComponent
                    labelKey={t("Configuration.downloadServerConfiguration.title")}
                    inputId={"title"}
                    value={formValues.downloadAppPageConfiguration.title}
                    onChange={(newValue) => handleInputChange("title", newValue)}
                ></InputComponent>

                <span className={classNames(style.inputContainer)}>
                    <label className={classNames(style.bmsLabel)}>
                        {t("Configuration.genericConfigurationForm.primaryColor")}
                    </label>
                    <div style={{ display: "flex", alignItems: "center", width: "31%" }}>
                        <input
                            disabled
                            type="text"
                            value={formValues.downloadAppPageConfiguration.primaryColor || ""}
                            className={classNames(style.brandInput)}
                            style={{
                                backgroundColor: formValues.downloadAppPageConfiguration.primaryColor || "",
                                color: getComplementaryColor(
                                    formValues.downloadAppPageConfiguration.primaryColor || ""
                                ),
                                width: "calc(100% - 32px)",
                                marginLeft: "0",
                            }}
                        />
                    </div>
                </span>
                <span className={classNames(style.inputContainer)}>
                    <label className={classNames(style.bmsLabel)}>
                        {t("Configuration.genericConfigurationForm.secondaryColor")}
                    </label>
                    <div style={{ display: "flex", alignItems: "center", width: "31%" }}>
                        <input
                            disabled
                            type="text"
                            value={formValues.downloadAppPageConfiguration.secondaryColor || ""}
                            className={classNames(style.brandInput)}
                            style={{
                                backgroundColor: formValues.downloadAppPageConfiguration.secondaryColor || "",
                                color: getComplementaryColor(
                                    formValues.downloadAppPageConfiguration.secondaryColor || ""
                                ),
                                width: "calc(100% - 32px)",
                                marginLeft: "0",
                            }}
                        />
                    </div>
                </span>
                <span className={classNames(style.inputContainer)}>
                    <label className={classNames(style.bmsLabel)}>
                        {t("Configuration.genericConfigurationForm.tertiaryColor")}
                    </label>
                    <div style={{ display: "flex", alignItems: "center", width: "31%" }}>
                        <input
                            disabled
                            type="text"
                            value={formValues.downloadAppPageConfiguration.tertiaryColor || ""}
                            className={classNames(style.brandInput)}
                            style={{
                                backgroundColor: formValues.downloadAppPageConfiguration.tertiaryColor || "",
                                color: getComplementaryColor(
                                    formValues.downloadAppPageConfiguration.tertiaryColor || ""
                                ),
                                width: "calc(100% - 32px)",
                                marginLeft: "0",
                            }}
                        />
                    </div>
                </span>

                <InputComponent
                    labelKey={t("Configuration.downloadServerConfiguration.androidLink")}
                    inputId={"androidLink"}
                    value={formValues.downloadAppPageConfiguration.androidFirebaseLink}
                    disabled={disableLinkEditing}
                    onChange={(newValue) => handleInputChange("androidFirebaseLink", newValue)}
                ></InputComponent>

                <InputComponent
                    labelKey={t("Configuration.downloadServerConfiguration.iOSLink")}
                    inputId={"iOSLink"}
                    value={formValues.downloadAppPageConfiguration.iOSFirebaseLink}
                    disabled={disableLinkEditing}
                    onChange={(newValue) => handleInputChange("iOSFirebaseLink", newValue)}
                ></InputComponent>

                <InputComponent
                    labelKey={t("Configuration.downloadServerConfiguration.licenseFileType")}
                    inputId={"licenseFileType"}
                    value={formValues.downloadAppPageConfiguration.licenseFileType}
                    onChange={(newValue) => handleInputChange("licenseFileType", newValue)}
                ></InputComponent>
                <Checkbox
                    labelKey={t("Configuration.downloadServerConfiguration.autoForward")}
                    toolTipContent={t("Configuration.tooltips.downloadServerConfiguration.autoForward")}
                    checkboxId={"checkContrast"}
                    checked={formValues.downloadAppPageConfiguration.autoForward || false}
                    onChange={(newValue: boolean) => handleInputChange("autoForward", newValue)}
                />
            </div>
        </div>
    );
};

export default DownloadServerConfigurationForm;
